import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Cookie } from "lucide-react";
import { buttons } from "./site/ui";

// LocalStorage keys (unchanged, so choices made on the old site still count).
export const CONSENT_KEY = "cookie_consent";
export const PREFS_KEY = "cookie_preferences";

const GA_ID = "G-4B12FQGE17";
let analyticsLoaded = false;

function loadAnalytics() {
  window[`ga-disable-${GA_ID}`] = false;
  if (analyticsLoaded) return;
  analyticsLoaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("consent", "default", { analytics_storage: "granted" });
  window.gtag("config", GA_ID);

  const tag = document.createElement("script");
  tag.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  tag.async = true;
  document.head.appendChild(tag);
}

// Stops Google Analytics and deletes the cookies it has already set.
export function disableAnalytics() {
  window[`ga-disable-${GA_ID}`] = true;
  if (window.gtag) window.gtag("consent", "update", { analytics_storage: "denied" });

  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = ["", host, `.${host}`];
  if (parts.length > 2) domains.push(`.${parts.slice(-2).join(".")}`);

  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!/^(_ga|_gid|_gat)/.test(name)) return;
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ""}`;
    });
  });
}

export function readPreferences() {
  const consent = localStorage.getItem(CONSENT_KEY);
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
  } catch {
    stored = {};
  }
  return {
    essential: true,
    analytics: typeof stored.analytics === "boolean" ? stored.analytics : consent === "granted" || consent === "granted_all",
  };
}

// Full-screen choice shown until the visitor decides. Google Analytics only
// loads after they allow it; rejecting removes any analytics cookies.
export default function CookieConsentOverlay() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [decided, setDecided] = useState(() => Boolean(localStorage.getItem(CONSENT_KEY)));
  const showing = !decided && pathname !== "/cookie-preferences";

  useEffect(() => {
    if (!decided) return;
    if (readPreferences().analytics) loadAnalytics();
    else disableAnalytics();
  }, [decided]);

  // Stop the page behind from scrolling while the choice is on screen.
  useEffect(() => {
    if (!showing) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showing]);

  const decide = (analytics) => {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ essential: true, analytics }));
    localStorage.setItem(CONSENT_KEY, analytics ? "granted_all" : "essential_only");
    setDecided(true);
  };

  if (!showing) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center overflow-y-auto bg-slate-900/70 p-4 print:hidden">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl sm:p-8"
      >
        <Cookie className="h-8 w-8 text-blue-600" aria-hidden="true" />
        <h2 id="cookie-title" className="mt-4 text-xl font-bold tracking-tight text-slate-900">
          Your cookie choices
        </h2>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          We use essential cookies to make this site work. With your permission, we'd also like to use Google
          Analytics to understand how people use it. You can change your mind at any time from the link in the footer.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse">
          <button type="button" onClick={() => decide(true)} className={`${buttons.primary} sm:flex-1`}>
            Accept all
          </button>
          <button type="button" onClick={() => decide(false)} className={`${buttons.secondary} sm:flex-1`}>
            Reject non-essential
          </button>
        </div>
        <button type="button" onClick={() => navigate("/cookie-preferences")} className={`${buttons.link} mt-4 text-sm`}>
          Manage preferences
        </button>
      </div>
    </div>
  );
}

